import classNames from 'classnames';
import PropTypes from 'prop-types';
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import {
  SquareCrossed,
} from '../../assets/js/svg/gui';
import { useTownAssets } from '../../stores';
import {
  appTooltipProps,
  displayIsoDate,
  imageSrcFromId,
} from '../../utils';
import { Button } from '../buttons';
import {
  Column,
  FullPopup,
  Row,
} from '../display';
import { SecuredImg } from '../graphics';

import TextInput from './TextInput';

function CompactImagePicker(props) {
  const {
    id,
    initialValue,
    pickingDone,
    canBeCleared,
    types,
    uploadType,
    onClose,
    altInput,
    initialAlt,
    altInputLabel,
  } = props;

  const acceptedTypes = (uploadType === 'comm')
    ? 'image/bmp, image/gif, image/jpeg, image/png, image/tiff, image/webp, image/x-windows-bmp'
    : 'image/bmp, image/gif, image/jpeg, image/png, image/tiff, image/webp, image/x-windows-bmp, image/svg+xml';

  const [imageIdPicked, setImageIdPicked] = useState(initialValue);
  const [altText, setAltText] = useState(initialAlt ?? '');
  const [newFile, setNewFile] = useState();
  const [newFileLabel, setNewFileLabel] = useState('');
  const [newFileSts, setNewFileSts] = useState('ready');

  const { assets } = useTownAssets();
  const {
    translate,
    imagesList,
    fetchImagesList,
    uploadImage,
  } = assets;

  const imagesFiltered = useMemo(
    () => {
      if (!imagesList) return undefined;

      return imagesList.filter((thisImage) => {
        if (!types) return true;

        return types.includes(thisImage.type);
      });
    },
    [imagesList, types],
  );

  const imagePicked = useMemo(
    () => {
      if (!imagesFiltered) return undefined;

      return imagesFiltered.find((thisImage) => (thisImage.id === imageIdPicked));
    },
    [imageIdPicked, imagesFiltered],
  );

  useEffect(
    () => { if (!imagesList) fetchImagesList(); },
    [fetchImagesList, imagesList],
  );

  const cancel = () => {
    if (onClose) onClose();
    setNewFile();
    setNewFileLabel('');
    setNewFileSts('ready');
  };

  const [uploadPopupOpen, setUploadPopupOpen] = useState(false);

  const fileOnInput = useCallback(
    async (event) => {
      if (event?.target?.files) {
        const file = event.target.files[0];

        event.target.value = null;
        setNewFile(file);
        setUploadPopupOpen(true);
      }
    },
    [],
  );

  const closeUploadPopup = useCallback(
    () => {
      setUploadPopupOpen(false);
      setNewFile();
      setNewFileLabel('');
    },
    [],
  );

  const addImage = useCallback(
    async () => {
      if (newFile) {
        setNewFileSts('busy');
        const fileInfo = {
          type: uploadType,
          label: newFileLabel.trim(),
        };

        const newImageId = await uploadImage(newFile, fileInfo);

        if (newImageId !== undefined) {
          setImageIdPicked(newImageId);
          fetchImagesList();
          setNewFileSts('ready');
        } else {
          setNewFileSts('error');
        }
        setNewFile();
        setNewFileLabel('');
        setUploadPopupOpen(false);
      }
    },
    [fetchImagesList, newFile, newFileLabel, uploadType, uploadImage],
  );

  const changeLabel = (event) => {
    setNewFileLabel(event.currentTarget.value);
  };

  const newFileLabelValid = newFileLabel?.trim().length > 2;

  const done = () => {
    if (pickingDone) {
      pickingDone({
        id,
        value: imageIdPicked,
        altText: altText.trim() || undefined,
      });
    }
    if (onClose) onClose();
    setNewFile();
    setNewFileLabel('');
    setNewFileSts('ready');
  };

  const clear = () => {
    if (pickingDone) {
      pickingDone({
        id,
        value: undefined,
      });
    }
    if (onClose) onClose();
  };

  const handleImageSelect = (image) => {
    setImageIdPicked(image?.id);
  };

  return (
    <FullPopup
      onClose={cancel}
      customSize
      title={translate('imageSelection')}
    >
      <Column className="pt-3 max-w-lg" crossStart gap={4}>

        {/* Selection preview */}
        <Row fullw className="bg-pxBgSubtle rounded p-3 border border-pxMainOutline" gap={3}>
          <div
            className={classNames(
              'w-16 h-16 shrink-0 rounded bg-checkers bg-[size:8px] border-2',
              {
                'border-pxPrimary': imageIdPicked,
                'border-pxOutlineStrong': !imageIdPicked,
              },
            )}
          >
            {imageIdPicked && (
              <SecuredImg
                urlPath={imageSrcFromId(imageIdPicked, true)}
                alt="preview"
                className="h-full w-full object-contain"
              />
            )}
            {(!imageIdPicked) && (
              <SquareCrossed className="h-full w-full object-contain text-pxTxtFaint" />
            )}
          </div>
          <Column crossStart gap={0} className="flex-1 min-w-0 text-nowrap">
            {imagePicked
              ? (
                  <>
                    <Row>
                      <span className="font-medium truncate w-full">{imagePicked.label}</span>
                      <span className="text-sm">{`(${imagePicked.filename})`}</span>
                    </Row>
                    <Row className="text-sm text-pxTxtMuted">
                      <b>{`${translate('recorded')}:`}</b>
                      <span>{displayIsoDate(imagePicked.updated)}</span>
                    </Row>
                    <Row className="text-sm text-pxTxtMuted">
                      <b>{`${translate('resolution')}:`}</b>
                      <span>{`${imagePicked.width} × ${imagePicked.height} px`}</span>
                    </Row>
                  </>
                )
              : (
                  <span className="text-pxTxtFaint italic">{translate('noSelection')}</span>
                )}
          </Column>
        </Row>

        {/* Image grid */}
        <Column crossStart gap={2} className="w-full min-h-32">
          <span className="text-sm font-medium text-pxTxtMuted">{translate('availableImages')}</span>
          <div className="grid grid-cols-6 gap-2 max-h-48 overflow-y-auto p-1 w-full">
            {imagesFiltered?.map((image) => (
              <button
                key={image.id}
                type="button"
                className={classNames(
                  'w-12 h-12 p-0.5 rounded bg-checkers bg-[size:8px] cursor-pointer transition-all',
                  {
                    'border-2 border-pxPrimary ring-2 ring-pxPrimary/30': imageIdPicked === image.id,
                    'border border-pxOutlineStrong hover:border-pxOutlineStrong': imageIdPicked !== image.id,
                  },
                )}
                onClick={() => handleImageSelect(image)}
                {...appTooltipProps(image.label)}
              >
                <SecuredImg
                  urlPath={imageSrcFromId(image.id, true)}
                  alt={image.label}
                  className="h-full w-full object-contain"
                />
              </button>
            ))}
          </div>
        </Column>

        {/* Upload section */}
        <Column
          className="w-full bg-pxInfoBg rounded p-3 border border-pxMainOutline"
          gap={3}
        >
          <span className="text-sm font-medium text-pxTxtMuted self-start">
            {translate('uploadNewImage')}
          </span>

          <Button
            label={translate('select')}
            id="selImage"
            fileOnInput={fileOnInput}
            disabled={newFileSts !== 'ready'}
            accept={acceptedTypes}
            baseColor
          />
        </Column>

        {/* Upload popup */}
        {(uploadPopupOpen && newFile) && (
          <FullPopup
            onClose={closeUploadPopup}
            small
          >
            <Column crossStart className="p-4 gap-4 min-w-72">
              <div className="w-32 h-32 mx-auto rounded bg-checkers bg-[size:8px] border border-pxOutlineStrong overflow-hidden">
                <img
                  src={URL.createObjectURL(newFile)}
                  alt="preview"
                  className="h-full w-full object-contain"
                />
              </div>

              <span className="text-sm text-pxTxtMuted truncate w-full">{newFile.name}</span>

              {(newFile.size > (1024 * 1024 * 4.9)) && (
                <span className="text-pxWarning text-sm">{translate('largeFilesRecompressed')}</span>
              )}

              <span className="text-pxUiAlert text-sm">
                {newFileLabelValid
                  ? ''
                  : `${translate('pleaseNameThisFile')}\u00A0:`}
              </span>

              <TextInput
                id="label"
                name="label"
                label="label"
                value={newFileLabel}
                onChange={changeLabel}
                invalid={newFileLabel.length > 0 && (!newFileLabelValid)}
                className="w-full"
              />

              <Row className="w-full justify-end" gap={3}>
                <Button
                  label={translate('cancel')}
                  onClick={closeUploadPopup}
                  neutralColor
                />
                <Button
                  label={translate('upload')}
                  id="addImage"
                  disabled={(newFileSts === 'busy') || (!newFileLabelValid)}
                  onClick={addImage}
                  pending={newFileSts === 'busy'}
                  imgSrc="/images/svg/gui/upload.svg"
                  baseColor
                />
              </Row>
            </Column>
          </FullPopup>
        )}

        {altInput && (
          <TextInput
            id="altText"
            name="altText"
            value={altText}
            onChange={(e) => setAltText(e.currentTarget.value)}
            className="w-full"
            placeholder={altInputLabel}
          />
        )}

        <Row fullw gap={4} className="p-2">
          <Button
            imgSrc="/images/svg/gui/back.svg"
            onClick={cancel}
            label={translate('cancel')}
            neutralColor
          />
          {canBeCleared && (
            <Button
              imgSrc="/images/svg/gui/trash.svg"
              onClick={clear}
              label={translate('toEmpty')}
              criticalColor
            />
          )}
          <Button
            imgSrc="/images/svg/gui/check.svg"
            onClick={done}
            disabled={(!canBeCleared) && (!imageIdPicked)}
            label={translate('confirm')}
            baseColor
          />
        </Row>
      </Column>
    </FullPopup>
  );
}

CompactImagePicker.propTypes = {
  id: PropTypes.string,
  initialValue: PropTypes.string,
  pickingDone: PropTypes.func,
  canBeCleared: PropTypes.bool,
  types: PropTypes.arrayOf(PropTypes.string),
  uploadType: PropTypes.string,
  onClose: PropTypes.func,
  altInput: PropTypes.bool,
  initialAlt: PropTypes.string,
  altInputLabel: PropTypes.string,
};

export default CompactImagePicker;
